import * as React from 'react';
import { useState } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UnsavedChangesLabels {
  cancel: string;
  confirm: string;
  description: string;
  title: string;
}

export interface UseUnsavedChangesGuardOptions {
  labels: UnsavedChangesLabels;
  onClose: () => void;
}

export interface UseUnsavedChangesGuardReturn {
  attemptClose: () => void;
  handleOpenChange: (nextOpen: boolean) => void;
  unsavedChangesDialog: React.JSX.Element;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useUnsavedChangesGuard<T extends FieldValues>(
  form: UseFormReturn<T>,
  { labels, onClose }: UseUnsavedChangesGuardOptions,
): UseUnsavedChangesGuardReturn {
  const [showDialog, setShowDialog] = useState(false);

  function attemptClose(): void {
    // Ignore re-triggers while the confirm dialog is already open.
    if (showDialog) return;

    if (form.formState.isDirty) {
      setShowDialog(true);
    } else {
      form.reset();
      onClose();
    }
  }

  function confirmDiscard(): void {
    setShowDialog(false);
    form.reset();
    onClose();
  }

  function handleOpenChange(nextOpen: boolean): void {
    if (!nextOpen) {
      attemptClose();
    }
  }

  const unsavedChangesDialog = (
    <AlertDialog onOpenChange={setShowDialog} open={showDialog}>
      <AlertDialogContent
        onCloseAutoFocus={(event): void => {
          // No trigger to restore focus to — Radix's body-focus fallback would make the underlying
          // Sheet treat it as an outside interaction and dismiss itself.
          event.preventDefault();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{labels.title}</AlertDialogTitle>
          <AlertDialogDescription>{labels.description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{labels.cancel}</AlertDialogCancel>
          <AlertDialogAction onClick={confirmDiscard}>{labels.confirm}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { attemptClose, handleOpenChange, unsavedChangesDialog };
}
